import { useMemo } from "react";
import { useAppStore } from "@/stores";
import { selectTournamentById } from "@/stores/tournament.store";
import type { PrizeSchema } from "@/types/generated-types";
import { useTournamentLeaderboard } from "./use-tournament-leaderboard";

export interface TournamentPrizeResult {
  earnedPrize: PrizeSchema | undefined;
  currentPrizeTarget: PrizeSchema | undefined;
  position: number | undefined;
  loading: boolean;
}

function positionInRange(
  position: number,
  placeFrom: number | null | undefined,
  placeTo: number | null | undefined
): boolean {
  if (placeFrom != null && position < placeFrom) return false;
  if (placeTo != null && position > placeTo) return false;
  return true;
}

export function useTournamentPrize(
  tournamentId?: string
): TournamentPrizeResult {
  const selectedId = useAppStore((state) => state.selectedCampaignId);
  const resolvedId = tournamentId ?? selectedId ?? "";

  const tournament = useAppStore((state) =>
    resolvedId ? selectTournamentById(state, resolvedId) : undefined
  );
  const { playerData, loading } = useTournamentLeaderboard(resolvedId);
  const position = playerData?.position;

  return useMemo((): TournamentPrizeResult => {
    const prizes = tournament?.prizes ?? [];
    if (position == null || position < 1) {
      const next = prizes
        .filter((p) => p.place_from != null && p.place_from > 0)
        .sort((a, b) => (a.place_from ?? 0) - (b.place_from ?? 0))[0];
      return {
        earnedPrize: undefined,
        currentPrizeTarget: next,
        position: undefined,
        loading,
      };
    }

    const earnedPrize = prizes.find((p) =>
      positionInRange(position, p.place_from, p.place_to)
    );
    if (earnedPrize) {
      return {
        earnedPrize,
        currentPrizeTarget: undefined,
        position,
        loading,
      };
    }

    const currentPrizeTarget = prizes
      .filter((p) => p.place_from != null && p.place_from > position)
      .sort((a, b) => (a.place_from ?? 0) - (b.place_from ?? 0))[0];

    return {
      earnedPrize: undefined,
      currentPrizeTarget,
      position,
      loading,
    };
  }, [tournament, position, loading]);
}
